import * as React from "react"
import { Helmet } from "react-helmet"
import { createGlobalStyle } from "styled-components"
import { motion } from "framer-motion"
import { Toaster } from "react-hot-toast"

import Subheader from "./Subheader"
import FixedNav from "./FixedNav"

import ContactUs from "./ContactUs"
import Footer from "./Footer"

import { useOutsideAlerter } from "../hooks/useOutsideAlerter"

const GlobalStyles = createGlobalStyle`

  body {
    font-size: 100%;
    font-family: Cabin, sans-serif;
    background-color: #FFFCF8;
    color: #424242;
    overflow-x: hidden;
  }

  *, *:before, *:after {
    margin: 0;
    padding: 0;
    box-sizing:border-box;
  }

  .tap-icons {
    padding: 8px;
  }
`

export type TNavState = "none" | "nav" | "search"

const variants = {
  initial: { x: 0 },
  pushLeft: { x: -350 },
}

const NEXT_STATE_GRAPH = {
  initial: {
    CLICK_SEARCH: "search",
    CLICK_BURGER: "nav",
  },
  nav: {
    CLICK_BURGER: "initial",
    CLICK_SEARCH: "search",
    CLICK_OUTSIDE: "initial",
  },
  search: {
    CLICK_OUTSIDE: "initial",
  },
}

function navReducer(state, event) {
  const nextState = NEXT_STATE_GRAPH[state][event]
  return nextState !== undefined ? nextState : state
}

const Page: React.FC<{
  children: React.ReactNode
  contactUsVisibility: "show" | "hide"
}> = ({ children, contactUsVisibility }) => {
  const navRef = React.useRef<HTMLElement>(null)
  const searchRef = React.useRef<HTMLElement>(null)

  const [state, send] = React.useReducer(navReducer, "initial")

  const clickBurger = () => send("CLICK_BURGER")
  const clickSearch = () => send("CLICK_SEARCH")
  const clickOutside = () => send("CLICK_OUTSIDE")

  useOutsideAlerter(navRef, state === "nav", clickOutside)
  useOutsideAlerter(searchRef, state === "search", clickOutside)

  return (
    <>
      <Toaster />
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Cabin:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&family=Tinos:ital,wght@0,400;0,700;1,400;1,700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <GlobalStyles />
      <FixedNav
        navRef={navRef}
        searchRef={searchRef}
        navState={state}
        toggleNav={clickBurger}
        setSearch={clickSearch}
      />
      <motion.main
        variants={variants}
        animate={state === "nav" || state === "search" ? "pushLeft" : "initial"}
        style={{
          position: "relative",
        }}
      >
        <Subheader />
        {children}
        {contactUsVisibility !== "show" ? null : <ContactUs />}
        <Footer />
      </motion.main>
    </>
  )
}

export default Page
